import * as React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { cn } from 'src/shared/helpers/utils';

export type AlertSeverity = 'info' | 'warning' | 'error' | 'success';

export type AlertProps = React.HTMLAttributes<HTMLDivElement> & {
    severity?: AlertSeverity;
    icon?: React.ReactNode;
};

const defaultSeverityIcons: Record<AlertSeverity, React.ReactNode> = {
    info: <Info className='size-[18px] shrink-0' aria-hidden />,
    warning: <AlertTriangle className='size-[18px] shrink-0' aria-hidden />,
    error: <AlertCircle className='size-[18px] shrink-0' aria-hidden />,
    success: <CheckCircle2 className='size-[18px] shrink-0' aria-hidden />,
};

// Text uses the design system's text tokens: orange-100 and green-100 on
// their tints fall short of WCAG AA (about 2.6:1).
const severityClasses: Record<AlertSeverity, string> = {
    info: 'border border-blue-20 bg-blue-0 text-system-info',
    warning: 'border border-orange-20 bg-orange-0 text-status-medium',
    error: 'border border-red-20 bg-red-0 text-status-very-high',
    success: 'border border-green-20 bg-green-0 text-status-very-low',
};

export default function Alert({ severity = 'info', icon, className, children, ...props }: AlertProps) {
    const resolvedIcon = icon !== undefined ? icon : defaultSeverityIcons[severity];

    return (
        <div
            role='alert'
            className={cn('w-full rounded-md p-3 text-sm flex items-start gap-2', severityClasses[severity], className)}
            {...props}
        >
            {resolvedIcon ? <span className='mt-0.5 shrink-0'>{resolvedIcon}</span> : null}
            <div className='flex-1'>{children}</div>
        </div>
    );
}
